import { EyeOpenIcon } from '@radix-ui/react-icons';
import { Tooltip } from 'radix-ui';
import React, { forwardRef } from 'react';

import { useTranslation } from '~/app';
import { ChatDisplayItem } from '~/app/selectors';
import { cn } from '~/utils/styles';

import ChatAvatar from './ChatAvatar';

type HeaderItem = ChatDisplayItem & {
  type: 'message';
  model: NonNullable<ChatDisplayItem['model']>;
};

const GroupSenderRow: React.FC<{
  item: HeaderItem;
  senderDisplayName: string;
}> = ({ item, senderDisplayName }) => {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1 @[18rem]:gap-2')}>
      <ChatAvatar
        userId={item.userId}
        username={senderDisplayName}
        size="sm"
        className="inline-flex h-2.5! w-2.5! shrink-0 text-[7px]! leading-none! @[18rem]:hidden"
      />
      <span className="inline-flex min-w-0 items-center gap-0.5">
        <span className="min-w-0 truncate text-xs font-bold text-gray-700 @[18rem]:text-sm">
          {senderDisplayName}
        </span>
        {item.isSpectator ? (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'pointer-events-auto inline-flex shrink-0 cursor-default items-center justify-center rounded border-0 bg-transparent p-0.5 align-middle',
                  '-m-0.5 text-slate-400/80 @[18rem]:text-slate-400/70',
                  'hover:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:outline-none',
                )}
                aria-label={t('chat_spectator_badge')}
              >
                <EyeOpenIcon
                  className="pointer-events-none h-2.5 w-2.5 @[18rem]:h-3 @[18rem]:w-3"
                  aria-hidden
                />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                sideOffset={6}
                className="z-400 rounded-lg border border-gray-100 bg-white px-2 py-1 text-xs text-slate-700 shadow-xl"
              >
                {t('chat_spectator_badge')}
                <Tooltip.Arrow className="fill-white" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ) : null}
      </span>
    </span>
  );
};

/** Full-width pinned strip; text alignment comes from inner rows (matches ChatMessageRow headers). */
const stickyMetaBlurClass = cn(
  'w-full min-w-full max-w-none rounded-none',
  'bg-white/70 backdrop-blur-sm supports-[backdrop-filter]:bg-white/65',
);

/** Compact: sticky header above bubbles (small avatar in row). */
export const ChatGroupStickyCompactBar: React.FC<{
  item: HeaderItem;
  senderDisplayName: string;
  headerPinned: boolean;
}> = ({ item, senderDisplayName, headerPinned }) => {
  const isOutgoing = item.model.direction === 'outgoing';
  return (
    <div
      className={cn(
        'pointer-events-none sticky inset-x-0 z-10 w-full min-w-0',
        /* Pull up over parent flex gap below sentinel (gap-0.5) so nothing scrolls through */
        '-top-px -mt-0.5',
        '@[18rem]:hidden',
      )}
    >
      <div
        data-chat-sticky-meta=""
        data-chat-sticky-pinned={headerPinned ? '' : undefined}
        className={cn(headerPinned && stickyMetaBlurClass)}
      >
        <div
          className={cn(
            'box-border pt-1 pb-0.5',
            'flex w-full min-w-0 items-center gap-1.5 text-[10px] text-gray-500',
            isOutgoing && 'justify-end text-right',
          )}
        >
          <span className={cn('min-w-0', isOutgoing && 'text-right')}>
            <GroupSenderRow item={item} senderDisplayName={senderDisplayName} />
          </span>
          <span className="shrink-0 opacity-80">{item.model.sentTime}</span>
        </div>
      </div>
    </div>
  );
};

export type ChatGroupStickyWideBarProps = {
  item: HeaderItem;
  senderDisplayName: string;
  headerPinned: boolean;
};

/**
 * Wide: incoming — name/time only (full width above avatar + bubbles); outgoing — name/time right, no avatar.
 * Ref on the sticky root avoids a short wrapper (which would shrink the sticky containing block and break the header).
 */
export const ChatGroupStickyWideBar = forwardRef<HTMLDivElement, ChatGroupStickyWideBarProps>(
  function ChatGroupStickyWideBar({ item, senderDisplayName, headerPinned }, ref) {
    const isOutgoing = item.model.direction === 'outgoing';
    return (
      <div
        ref={ref}
        className={cn(
          'pointer-events-none sticky inset-x-0 z-10 hidden w-full min-w-0',
          '-top-px @[18rem]:-mt-1 @[18rem]:block',
        )}
      >
        <div
          data-chat-sticky-meta=""
          data-chat-sticky-pinned={headerPinned ? '' : undefined}
          className={cn(headerPinned && stickyMetaBlurClass)}
        >
          {isOutgoing ? (
            <div className="box-border flex w-full min-w-0 items-center justify-end gap-2 pt-1 pb-0.5 text-[10px] text-gray-500">
              <span className="min-w-0 truncate text-right">
                <GroupSenderRow item={item} senderDisplayName={senderDisplayName} />
              </span>
              <span className="shrink-0 opacity-100">{item.model.sentTime}</span>
            </div>
          ) : (
            <div className="mb-0.5 box-border flex w-full min-w-0 items-center gap-1.5 pt-1 pb-0.5 text-[10px] text-gray-500 @[18rem]:gap-2">
              <span className="min-w-0">
                <GroupSenderRow item={item} senderDisplayName={senderDisplayName} />
              </span>
              <span className="shrink-0 opacity-100">{item.model.sentTime}</span>
            </div>
          )}
        </div>
      </div>
    );
  },
);
